import { NanoEvent } from './NanoEvent.js'

let idCounter = 10000
export const MessageType = {
  SYS: 'Sys', // 系统消息，一般不显示
  TEXT: 'Text',
  CODE: 'Code',
  IMAGE: 'Image',
  OUTPUT: 'Output',
  CHOICE: 'Choice',
  CHECKPOINT: 'Checkpoint',
  CHECKPOINTS: 'Checkpoints',
  FILE: 'File',
  VIDEO: 'Video',
  TEST: 'Test',
  BRANCH: 'Branch',
  NOTICE: 'Notice',
  PK: 'Pk',                         // 学校比赛
  MATCH_USER: 'MatchUser',          // 匹配学习队友/对手
  PROGRAMMING: 'Programming',       // 编程题
  QUICK_SELECT: 'QuickSelect',      // 快速知识点抢答
  QUICK_CHOICES: 'QuickChoices'     // 快速选择题
}

export const MessageName = {
  AI: 'ai',
  TEACHER: 'teacher'
}

export const TestType = {
  JUPYTER: 'jupyter'
}

export const TestStatus = {
  PASS: 'Pass',
  PENDING: 'Pending',
  ERROR: 'Error',
  SKIP: 'Skip'
}

// MessageId 定义
// 0
// 1-999 系统消息
// 1 已连线
// 2 欢迎
// 404 课程内容不存在
// 999 课程已学完
// 1000-9999 课程消息
// 10001-99999 普通消息

class Message extends NanoEvent {
  constructor (name, type, data, meta = {}) {
    super()
    if (!name) throw new Error('消息发送者未定义')
    if (!type) throw new Error('消息类型未定义')
    if (meta.id !== void 0) {
      this.id = meta.id
    } else {
      this.id = idCounter++
    }

    this.innerId = idCounter++

    this.time = new Date()
    this.name = name
    this.type = type
    this.next = meta.next
    this.dist = meta.dist
    this.status = 'loading'
    this.repeats = 1
    this._data = {}
    this.fields = []

    const that = this
    Object.defineProperty(this, 'data', {
      enumerable: true, // 可枚举
      configurable: false, // 不能再define
      get: function() {
        return this._data
      },
      set: function(newVal) {
        if (typeof newVal !== 'object') throw new Error('消息数据必须是一个对象')
        this.status = ''
        this._data = that._parseData(newVal)
        that.$emit('loaded', that)
      }
    })

    if (data) {
      this.data = data
    }
  }

  isSame (other) {
    if (other.type !== this.type || other.name !== this.name) return false
    for (let i = 0; i < this.fields.length; i++) {
      if (JSON.stringify(other._data[this.fields[i]]) !== JSON.stringify(this._data[this.fields[i]])) {
        return false
      }
    }
    return true
  }

  _parseData (data) {
    return data
  }

  toJSON () {
    return {
      name: this.name,
      type: this.type,
      data: this._data,
      id: this.id
    }
  }
}

// 文件传输
export class FileMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.FILE, data, meta)
    this.fields = ['filename']
  }
}

// 带测试的消息
export class TestMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.TEST, data, meta)
    this.fields = ['status', 'title', 'text', 'tests', 'results']
    if (!data.results) data.results = []
    if (!data.status) data.status = ''
  }

  static isPass (message) {
    return [TestStatus.SKIP, TestStatus.PASS].includes(message.data.status)
  }

  static isSkipped (message) {
    return [TestStatus.SKIP].includes(message.data.status)
  }
}

// 选择题消息 HTML
export class ChoiceMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.CHOICE, data, meta)
    this.fields = ['content', 'choices', 'answer', 'choose', 'explanation']
  }
}

// 普通文本消息 HTML
export class TextMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.TEXT, data, meta)
    this.fields = ['text', 'time']
  }
}

// 检查点消息，不显示
export class CheckpointMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.CHECKPOINT, data, meta)
    this.fields = ['text']
  }
}

export class CheckpointsMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.CHECKPOINTS, data, meta)
    this.fields = ['checkpoints']
  }
}

// 分支消息
export class BranchMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.BRANCH, data, meta)
    this.fields = ['branches']
  }
}

// 通知消息
export class NoticeMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.NOTICE, data, meta)
    this.fields = ['text', 'time']
  }
}

// Pk 消息
export class PkMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.PK, data, meta)
    this.fields = ['name', 'image', 'cookbook', 'winNoty', 'failNoty']
  }
}

export class SysMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.SYS, data, meta)
    this.fields = ['text']
  }
}

// 抢答单选题
export class QuickChoicesMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.QUICK_CHOICES, data, meta)
    this.fields = ['limit', 'choices']
  }
}

// 知识点抢答
export class QuickSelectMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.QUICK_SELECT, data, meta)
    this.fields = ['content', 'choice']
  }
}

// 编程题
export class ProgrammingMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.PROGRAMMING, data, meta)
    this.fields = ['content', 'problem']
  }
}

// 程序输出结果
export class OutputMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.OUTPUT, data, meta)
    this.fields = ['input', 'output', 'error', 'codeId']
  }

  _parseData (data) {
    if (data.error) {
      data.error = data.error.replace('SyntaxError', '语法错误')
      data.error = data.error.replace('ImportError: No module named <stdin> at <unknown>', '代码为空,无运行结果')
    }
    return data
  }
}

// 代码段
export class CodeMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.CODE, data, meta)
    this.fields = ['code', 'input', 'type', 'desc', 'attachments', 'image', 'fill']
    this._popup = false
  }

  isSame (other) {
    if (other.type !== this.type || other.name !== this.name) return false

    if (this.data.output && other.data.output && this.data.output.data.error && this.data.output.data.error === other.data.output.data.error) {
      return true
    }
    return false
  }
}

// 图片
export class ImageMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.IMAGE, data, meta)
    this.fields = ['src']
  }
}

// 视频
export class VideoMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.VIDEO, data, meta)
    this.fields = ['src']
  }
}

// 匹配队友/对手
export class MatchUserMessage extends Message {
  constructor (name, data, meta) {
    super(name, MessageType.MATCH_USER, data, meta)
    this.fields = []
  }
}

const Class = {
  ChoiceMessage,
  CheckpointMessage,
  CheckpointsMessage,
  TextMessage,
  OutputMessage,
  CodeMessage,
  ImageMessage,
  BranchMessage,
  TestMessage,
  SysMessage,
  FileMessage,
  VideoMessage,
  NoticeMessage,
  QuickChoicesMessage,
  QuickSelectMessage,
  ProgrammingMessage,
  MatchUserMessage,
  PkMessage
}

export function MessageFactory (name, type, data, meta = {}) {
  const MessageClass = Class[type + 'Message']
  const message = new MessageClass(name, data, meta)
  if (meta.time) {
    message.time = new Date(meta.time)
  }
  return message
}
